import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { toast } from "react-hot-toast";
import { v4 as uuid } from "uuid";
import { useNavigate, useLocation, Link } from "react-router-dom";
import "./Home.css";
import 'bootstrap/dist/css/bootstrap.min.css';
import 'font-awesome/css/font-awesome.min.css';
import { initSocket } from "../socket.js";

function Home() {
  const [joinRoomId, setJoinRoomId] = useState("");
  const [createdRoomId, setCreatedRoomId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [recentRooms, setRecentRooms] = useState([]);
  const [showInstructions, setShowInstructions] = useState(false);
  const [justCreated, setJustCreated] = useState(false);
  const [socket, setSocket] = useState(null);
  // Room existence check states
  const [roomExists, setRoomExists] = useState(null); // null = unknown, true/false = exists/doesn't exist
  const [checkingRoom, setCheckingRoom] = useState(false);
  const [roomCheckTimeout, setRoomCheckTimeout] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  // Refs to avoid stale closures in socket handlers
  const joinRoomIdRef = useRef(joinRoomId);
  const firstNameRef = useRef(firstName);
  const lastNameRef = useRef(lastName);
  const emailRef = useRef(email);
  const justCreatedRef = useRef(justCreated);

  useEffect(() => {
    joinRoomIdRef.current = joinRoomId;
    firstNameRef.current = firstName;
    lastNameRef.current = lastName;
    emailRef.current = email;
    justCreatedRef.current = justCreated;
  }, [joinRoomId, firstName, lastName, email, justCreated]);

  // Initialize socket
  useEffect(() => {
    let isMounted = true;
    const init = async () => {
      const s = await initSocket();
      if (isMounted) setSocket(s);
    };
    init();
    return () => {
      isMounted = false;
      if (socket) socket.disconnect();
    };
  }, []);

  // Socket event listeners
  useEffect(() => {
    if (!socket) return;

    const handleJoined = (data) => {
      setLoading(false);
      // Save to recent rooms
      const newRoom = {
        id: joinRoomIdRef.current,
        name: joinRoomIdRef.current,
        timestamp: Date.now(),
      };
      const updated = [newRoom, ...recentRooms.filter(r => r.id !== joinRoomIdRef.current)].slice(0, 10);
      setRecentRooms(updated);
      localStorage.setItem(`v-editor-recent-rooms-${emailRef.current}`, JSON.stringify(updated));

      navigate(`/editor/${joinRoomIdRef.current}`, {
        state: {
          username: `${firstNameRef.current} ${lastNameRef.current}`,
          email: emailRef.current,
          firstName: firstNameRef.current,
          lastName: lastNameRef.current,
          isCreator: justCreatedRef.current,
        },
      });
      setJustCreated(false);
    };

    const handleJoinDenied = () => {
      setLoading(false);
      toast.error("❌ Failed to join room");
    };

    const handleRoomInvalid = () => {
      setLoading(false);
      setRoomExists(false);
      toast.error("Room does not exist. Please check the Room ID and try again.");
      setJustCreated(false);
    };

    const handleRoomExists = ({ exists }) => {
      setCheckingRoom(false);
      setRoomExists(exists);
      if (!exists) {
        toast.error("This room does not exist. Please check the Room ID.");
      }
    };

    socket.on("joined", handleJoined);
    socket.on("JOIN_DENIED", handleJoinDenied);
    socket.on("ROOM_INVALID", handleRoomInvalid);
    socket.on("ROOM_EXISTS", handleRoomExists);

    return () => {
      socket.off("joined", handleJoined);
      socket.off("JOIN_DENIED", handleJoinDenied);
      socket.off("ROOM_INVALID", handleRoomInvalid);
      socket.off("ROOM_EXISTS", handleRoomExists);
    };
  }, [socket, navigate, recentRooms]);

  // Check room existence with debounce
  useEffect(() => {
    // Clear previous timeout
    if (roomCheckTimeout) {
      clearTimeout(roomCheckTimeout);
    }

    // Reset existence when room ID is empty
    if (!joinRoomId.trim()) {
      setRoomExists(null);
      setCheckingRoom(false);
      return;
    }

    // Don't check if we just created this room
    if (justCreated) {
      setRoomExists(null);
      setCheckingRoom(false);
      return;
    }

    // Set checking state and create new timeout
    setCheckingRoom(true);
    const timeout = setTimeout(() => {
      if (socket && joinRoomId.trim()) {
        socket.emit("check-room-exists", { roomId: joinRoomId.trim() });
      } else {
        setCheckingRoom(false);
      }
    }, 500); // Debounce for 500ms

    setRoomCheckTimeout(timeout);

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [joinRoomId, justCreated, socket]);

  // Load user data from location state
  useEffect(() => {
    if (location.state) {
      setFirstName(location.state.firstName || "");
      setLastName(location.state.lastName || "");
      setEmail(location.state.email || "");
      if (location.state.firstName) {
        toast.success(`Welcome back, ${location.state.firstName}! 👋`);
      }
    }
  }, [location.state]);

  // Load recent rooms when email changes
  useEffect(() => {
    if (!email) return;
    const saved = localStorage.getItem(`v-editor-recent-rooms-${email}`);
    if (saved) setRecentRooms(JSON.parse(saved));
  }, [email]);

  // Generate a new room ID
  const generateRoomId = (e) => {
    e.preventDefault();
    if (createdRoomId.trim()) {
      toast("Manual Room ID already entered");
      return;
    }
    const id = uuid().split("-")[0];
    setCreatedRoomId(id);
    setJoinRoomId(id);
    setJustCreated(true);
    setRoomExists(null); // Reset existence check for new room

    navigator.clipboard.writeText(id).catch(() => {
      toast.error("Failed to copy. Please copy manually.");
    });
    toast.success("New room created & copied 🎉");
  };

  // Copy functions with error handling
  const copyToClipboard = (text, setCopied) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      toast.success("Room ID copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      toast.error("Failed to copy. Please copy manually.");
    });
  };

  const [copiedJoin, setCopiedJoin] = useState(false);
  const [copiedCreate, setCopiedCreate] = useState(false);

  const copyJoinId = () => {
    if (!joinRoomId) return;
    copyToClipboard(joinRoomId, setCopiedJoin);
  };

  const copyCreatedId = () => {
    if (!createdRoomId) return;
    copyToClipboard(createdRoomId, setCopiedCreate);
  };

  // Join room (main action)
  const joinRoom = () => {
    if (!socket) {
      toast.error("Connection not established. Please try again.");
      setLoading(false);
      return;
    }
    if (!joinRoomId.trim() || !firstName.trim() || !lastName.trim()) {
      toast.error("Please fill in all required fields");
      setLoading(false);
      return;
    }

    // If we know the room doesn't exist, show error
    if (roomExists === false && !justCreated) {
      toast.error("Cannot join: This room does not exist");
      return;
    }

    setLoading(true);

    socket.emit("join", {
      roomId: joinRoomId.trim(),
      username: `${firstName} ${lastName}`,
      email,
      isCreator: justCreated,
      password: null, // No password needed
    });
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    joinRoom();
  };

  const handleQuickJoin = (room) => {
    setJoinRoomId(room.id);
    setJustCreated(false);
    toast.success(`Room ID ${room.id} loaded!`);
  };

  const handleClearRecent = () => {
    setRecentRooms([]);
    if (email) {
      localStorage.removeItem(`v-editor-recent-rooms-${email}`);
    }
    toast.success("Recent rooms cleared!");
  };

  const handleLogout = () => {
    toast.success("Logged out successfully! 👋");
    setTimeout(() => {
      navigate("/");
    }, 500);
  };

  // Room status indicator component
  const RoomStatusIndicator = () => {
    if (!joinRoomId.trim() || justCreated) return null;
    
    if (checkingRoom) {
      return (
        <div className="room-status checking">
          <span className="spinner-border spinner-border-sm me-1" />
          Checking room...
        </div>
      );
    }
    
    if (roomExists === true) {
      return (
        <div className="room-status exists">
          <i className="fa fa-check-circle me-1" />
          Room exists ✓
        </div>
      );
    }
    
    if (roomExists === false) {
      return (
        <div className="room-status not-exists">
          <i className="fa fa-exclamation-triangle me-1" />
          Room does not exist
        </div>
      );
    }
    
    return null;
  };

  // Welcome Modal (inline for brevity, but can be extracted)
  const WelcomeModal = () => {
    if (!showInstructions) return null;
    return (
      <div className="modal-overlay" onClick={() => setShowInstructions(false)}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h5><i className="fa fa-info-circle me-2" />Welcome to V-Editor! 🎉</h5>
            <button className="modal-close" onClick={() => setShowInstructions(false)}>&times;</button>
          </div>
          <div className="modal-body">
            <div className="instructions">
              <div className="instruction-item">
                <i className="fa fa-plus-circle text-primary" />
                <div><h6>Create New Room</h6><p>Click "New Room" to generate a unique room ID for collaboration</p></div>
              </div>
              <div className="instruction-item">
                <i className="fa fa-sign-in-alt text-success" />
                <div><h6>Join Existing Room</h6><p>Enter a room ID and your name to join an existing editing session</p></div>
              </div>
              <div className="instruction-item">
                <i className="fa fa-users text-warning" />
                <div><h6>Collaborate</h6><p>Share the room ID with others to collaborate in real-time</p></div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="modal-btn btn-primary" onClick={() => setShowInstructions(false)}>Got it! Let's start</button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="home-page">
      <WelcomeModal />

      <header className="home-header">
        <div className="container">
          <div className="header-content">
            <div className="brand">
              <img className="home-logo" src="/images/logos.png" alt="v_editor_logo"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Y2lyY2xlIGN4PSIyMCIgY3k9IjIwIiByPSIyMCIgZmlsbD0iIzQzNjFFRSIvPjxwYXRoIGQ9Ik0xMCAyMEwyMCAxNUwzMCAyMEwyMCAyNUwxMCAyMFoiIGZpbGw9IndoaXRlIi8+PC9zdmc+";
                }}
              />
              <div>
                <h1>V-Editor</h1>
                <p className="tagline">Real-time collaborative code editing</p>
              </div>
            </div>
            <div className="user-info">
              <div className="user-details">
                <div className="avatar">{firstName ? firstName.charAt(0).toUpperCase() : 'U'}</div>
                <div>
                  <strong>{firstName} {lastName}</strong>
                  <small>{email}</small>
                </div>
              </div>
              <button onClick={handleLogout} className="logout-btn">
                <i className="fa fa-sign-out-alt" /> Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="home-main">
        <div className="container">
          <div className="home-content">
            <div className="home-card">
              <div className="home-card-header">
                <h2><i className="fa fa-code me-2" />Start Collaborating</h2>
                <button className="help-btn" onClick={() => setShowInstructions(true)}>
                  <i className="fa fa-question-circle" /> How it works
                </button>
              </div>

              <div className="home-card-body">
                <form onSubmit={handleFormSubmit}>
                  <div className="form-section">
                    <h5><i className="fa fa-user-circle me-2" />Your Information</h5>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label htmlFor="firstName"><i className="fa fa-user" /> First Name</label>
                          <input id="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} type="text" className="form-control" placeholder="Enter your first name" required />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label htmlFor="lastName"><i className="fa fa-user" /> Last Name</label>
                          <input id="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} type="text" className="form-control" placeholder="Enter your last name" required />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="form-section">
                    <h5><i className="fa fa-door-open me-2" />Join an existing room</h5>
                    <div className="form-group">
                      <label htmlFor="joinRoomId"><i className="fa fa-key" /> Room ID to join</label>
                      <div className="input-with-action">
                        <input
                          id="joinRoomId"
                          value={joinRoomId}
                          onChange={(e) => {
                            setJoinRoomId(e.target.value.trim());
                            setJustCreated(false); // manual edit means not newly created
                          }}
                          type="text"
                          className="form-control"
                          placeholder="Enter an existing room ID"
                          required
                        />
                        <div className="input-actions">
                          <button type="button" className="action-btn copy-btn" onClick={copyJoinId} disabled={!joinRoomId} title="Copy Room ID">
                            <i className={`fa ${copiedJoin ? 'fa-check' : 'fa-copy'}`} />
                          </button>
                        </div>
                      </div>
                      <RoomStatusIndicator />
                      <div className="form-text">Paste a room ID you received or choose from recent rooms below</div>
                    </div>
                  </div>

                  <div className="form-section">
                    <h5><i className="fa fa-plus-circle me-2" />Create a new room</h5>
                    <div className="form-group">
                      <label htmlFor="createdRoomId"><i className="fa fa-key" /> Generated Room ID</label>
                      <div className="input-with-action">
                        <input
                          id="createdRoomId"
                          value={createdRoomId}
                          onChange={(e) => {
                            setCreatedRoomId(e.target.value.trim());
                            setJoinRoomId(e.target.value.trim());
                            setJustCreated(true);
                          }}
                          type="text"
                          className="form-control"
                          placeholder="Enter your own room ID or generate"
                        />
                        <div className="input-actions">
                          <button type="button" className="action-btn copy-btn" onClick={copyCreatedId} disabled={!createdRoomId} title="Copy Generated ID">
                            <i className={`fa ${copiedCreate ? 'fa-check' : 'fa-copy'}`} />
                          </button>
                        </div>
                      </div>
                      <div className="form-text">Click the button below to generate a new room ID (automatically copied and filled into the join field)</div>
                    </div>
                  </div>

                  <div className="form-actions">
                    <button 
                      type="submit" 
                      className="join-btn" 
                      disabled={loading || !joinRoomId || !firstName || !lastName || (roomExists === false && !justCreated)}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Joining Room...
                        </>
                      ) : (
                        <>
                          <i className="fa fa-sign-in-alt me-2" />
                          Join Room
                        </>
                      )}
                    </button>
                    <button type="button" className="new-room-btn" onClick={generateRoomId}>
                      <i className="fa fa-plus-circle me-2" /> New Room
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {recentRooms.length > 0 && (
              <div className="recent-rooms-card">
                <div className="card-header">
                  <h5><i className="fa fa-history me-2" />Recent Rooms</h5>
                  <button className="clear-btn" onClick={handleClearRecent}>Clear All</button>
                </div>
                <div className="recent-rooms-list">
                  {recentRooms.map((room, index) => (
                    <div key={index} className="recent-room-item" onClick={() => handleQuickJoin(room)}>
                      <div className="room-info">
                        <i className="fa fa-hashtag" />
                        <div>
                          <strong>{room.name}</strong>
                          <small>{new Date(room.timestamp).toLocaleDateString()}</small>
                        </div>
                      </div>
                      <button className="quick-join-btn"><i className="fa fa-arrow-right" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

         <div className="features-section">
            <h3 className="section-title">
              <i className="fa fa-star me-2" />
              Why Choose V-Editor?
            </h3>
            <div className="row">
              <div className="col-md-4">
                <div className="feature-card">
                  <div className="feature-icon real-time">
                    <i className="fa fa-bolt" />
                  </div>
                  <h5>Real-time Editing</h5>
                  <p>See changes instantly as multiple users collaborate simultaneously</p>
                </div>
              </div>
              <div className="col-md-4">
                <div className="feature-card">
                  <div className="feature-icon secure">
                    <i className="fa fa-shield-alt" />
                  </div>
                  <h5>Secure Rooms</h5>
                  <p>Unique room IDs ensure only invited collaborators can join</p>
                </div>
              </div>
              <div className="col-md-4">
                <div className="feature-card">
                  <div className="feature-icon multi">
                    <i className="fa fa-users" />
                  </div>
                  <h5>Multi-language</h5>
                  <p>Supports multiple programming languages with syntax highlighting</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <footer className="home-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-info">
              <p>© {new Date().getFullYear()} V-Editor. All rights reserved.</p>
              <p className="version">Version 1.0.1</p>
            </div>
            <div className="footer-links">
              <Link to="/help" className="footer-link"><i className="fa fa-question-circle" /> Help</Link>
              <Link to="/privacy" className="footer-link"><i className="fa fa-shield-alt" /> Privacy</Link>
              <Link to="/terms" className="footer-link"><i className="fa fa-file-text" /> Terms</Link>
              <Link to="/contact" className="footer-link"><i className="fa fa-envelope" /> Contact</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

Home.propTypes = {
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  email: PropTypes.string,
};

export default Home;